import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import {
  resolveChatUserBubbleTextColor,
  setPublicBrandingQueryData,
} from '@/features/shared/brand/branding-core'
import { getWorkspaceBackgroundVeil } from '@/features/shared/brand/workspace-background-veil'
import { cn } from '@/lib/cn'

import {
  deleteWorkspaceBackground,
  getWorkspaceBackgroundSourceBlob,
  publishWorkspaceBackground,
  type AdminSystemSettings,
  type WorkspaceBackgroundCrop,
  type WorkspaceBackgroundVariant,
} from './api'

const queryKey = ['admin-branding-settings']
const variants: WorkspaceBackgroundVariant[] = ['desktop', 'laptop', 'mobile']
const defaultCrop: WorkspaceBackgroundCrop = { focus_x: 0.5, focus_y: 0.5, zoom: 1 }
const deviceResolutions: Record<WorkspaceBackgroundVariant, string> = {
  desktop: '1920×1080',
  laptop: '1440×900',
  mobile: '390×844',
}

interface WorkspaceBackgroundDraft {
  baseColor: string
  crops: Record<WorkspaceBackgroundVariant, WorkspaceBackgroundCrop>
  enabled: boolean
  imageOpacity: number
  mobileConversationMode: 'background' | 'base_color'
}

function readDraft(settings: AdminSystemSettings): WorkspaceBackgroundDraft {
  const manifest = settings.config.workspace_background
  const storedCrops = settings.config.workspace_background_source?.crops

  return {
    baseColor: manifest?.base_color ?? '#F8FAFE',
    crops: {
      desktop: storedCrops?.desktop ?? defaultCrop,
      laptop: storedCrops?.laptop ?? defaultCrop,
      mobile: storedCrops?.mobile ?? defaultCrop,
    },
    enabled: manifest?.enabled ?? true,
    imageOpacity:
      manifest?.image_opacity ??
      (manifest?.intensity_preset === 'clear'
        ? 0.45
        : manifest?.intensity_preset === 'subtle'
          ? 0.24
          : 0.32),
    mobileConversationMode: manifest?.mobile_conversation_mode ?? 'base_color',
  }
}

function readOptionalTrimmedText(value: string | null | undefined) {
  const normalized = value?.trim()

  return normalized?.length ? normalized : undefined
}

function useFileUrl(file: File | null) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const nextUrl = file ? URL.createObjectURL(file) : null
    queueMicrotask(() => {
      if (active) setUrl(nextUrl)
    })
    return () => {
      active = false
      if (nextUrl) URL.revokeObjectURL(nextUrl)
    }
  }, [file])

  return url
}

function useStoredSourceUrl(enabled: boolean, sourceType: 'mobile_override' | 'primary') {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return undefined

    let active = true
    let objectUrl: string | undefined
    void getWorkspaceBackgroundSourceBlob(sourceType)
      .then(({ blob }) => {
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => {
        if (active) setUrl(null)
      })

    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [enabled, sourceType])

  return enabled ? url : null
}

export function WorkspaceBackgroundEditor({
  settings,
  userBubbleColor,
}: {
  settings: AdminSystemSettings
  userBubbleColor: string
}) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(() => readDraft(settings))
  const [device, setDevice] = useState<WorkspaceBackgroundVariant>('desktop')
  const [previewState, setPreviewState] = useState<'conversation' | 'empty'>('empty')
  const [primaryFile, setPrimaryFile] = useState<File | null>(null)
  const [mobileFile, setMobileFile] = useState<File | null>(null)
  const [removeMobileOverride, setRemoveMobileOverride] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const primaryInputRef = useRef<HTMLInputElement | null>(null)
  const mobileInputRef = useRef<HTMLInputElement | null>(null)

  const hasPrimarySource = Boolean(settings.config.workspace_background_source?.primary?.key)
  const hasMobileOverride = Boolean(
    settings.config.workspace_background_source?.mobile_override?.key,
  )
  const primaryFileUrl = useFileUrl(primaryFile)
  const mobileFileUrl = useFileUrl(mobileFile)
  const storedPrimaryUrl = useStoredSourceUrl(hasPrimarySource, 'primary')
  const storedMobileUrl = useStoredSourceUrl(
    hasMobileOverride && !removeMobileOverride,
    'mobile_override',
  )
  const fallbackVariant = settings.config.workspace_background?.variants?.[device]
  const fallbackUrl = fallbackVariant?.webp_url ?? fallbackVariant?.jpeg_url ?? null
  const primaryPreviewUrl = primaryFileUrl ?? storedPrimaryUrl ?? fallbackUrl
  const mobilePreviewUrl =
    mobileFileUrl ??
    storedMobileUrl ??
    (removeMobileOverride ? primaryPreviewUrl : (fallbackUrl ?? primaryPreviewUrl))
  const previewUrl = device === 'mobile' ? mobilePreviewUrl : primaryPreviewUrl
  const currentCrop = draft.crops[device]
  const configuredWelcomeTitle = readOptionalTrimmedText(settings.config.hub_agent_welcome_title)

  const resetEditorDraft = () => {
    setDraft(readDraft(settings))
    setDevice('desktop')
    setPreviewState('empty')
    setPrimaryFile(null)
    setMobileFile(null)
    setRemoveMobileOverride(false)
  }

  const openEditor = () => {
    resetEditorDraft()
    setEditorOpen(true)
  }

  const closeEditor = () => {
    setEditorOpen(false)
    setResetOpen(false)
    resetEditorDraft()
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      publishWorkspaceBackground({
        baseColor: draft.baseColor,
        crops: draft.crops,
        enabled: draft.enabled,
        imageOpacity: draft.imageOpacity,
        mobileConversationMode: draft.mobileConversationMode,
        mobileFile,
        primaryFile,
        removeMobileOverride,
      }),
    onError: () => {
      showFeedback({ message: t('branding.workspaceBackground.saveFailed'), tone: 'error' })
    },
    onSuccess: async (result) => {
      setPublicBrandingQueryData(queryClient, result.config)
      setEditorOpen(false)
      setPrimaryFile(null)
      setMobileFile(null)
      setRemoveMobileOverride(false)
      await queryClient.invalidateQueries({ queryKey })
      showFeedback({ message: t('branding.workspaceBackground.saved'), tone: 'success' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteWorkspaceBackground,
    onError: () => {
      showFeedback({ message: t('branding.workspaceBackground.deleteFailed'), tone: 'error' })
    },
    onSuccess: async (result) => {
      setPublicBrandingQueryData(queryClient, result.config)
      setResetOpen(false)
      setEditorOpen(false)
      await queryClient.invalidateQueries({ queryKey })
      showFeedback({ message: t('branding.workspaceBackground.deleted'), tone: 'success' })
    },
  })

  const updateCrop = (updates: Partial<WorkspaceBackgroundCrop>) => {
    setDraft((current) => ({
      ...current,
      crops: {
        ...current.crops,
        [device]: { ...current.crops[device], ...updates },
      },
    }))
  }

  const selectFile = (
    event: ChangeEvent<HTMLInputElement>,
    setter: (file: File | null) => void,
  ) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    if (!file) return
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      showFeedback({ message: t('branding.workspaceBackground.invalidFile'), tone: 'error' })
      return
    }
    setter(file)
  }

  const canSave = hasPrimarySource || Boolean(primaryFile)

  return (
    <section className="grid gap-5 rounded-lg border border-[var(--color-border)] bg-white p-5 shadow-[0_1px_2px_rgba(26,28,25,0.04)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid gap-1">
          <h2 className="text-base font-bold text-[var(--color-text)]">
            {t('branding.workspaceBackground.title')}
          </h2>
          <p className="text-sm leading-6 text-[var(--color-text-muted)]">
            {t('branding.workspaceBackground.description')}
          </p>
        </div>
        <Button onClick={openEditor} variant="secondary">
          {t('branding.workspaceBackground.configure')}
        </Button>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3">
        <div
          aria-hidden
          className="h-14 w-24 shrink-0 overflow-hidden rounded-md border border-[var(--color-border)] bg-white bg-cover bg-center"
          style={{
            backgroundColor: settings.config.workspace_background?.base_color ?? '#F8FAFE',
            backgroundImage: primaryPreviewUrl ? `url(${primaryPreviewUrl})` : undefined,
          }}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[var(--color-text)]">
            {hasPrimarySource
              ? t('branding.workspaceBackground.configured')
              : t('branding.workspaceBackground.notConfigured')}
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t('branding.workspaceBackground.previewBaselines')}
          </p>
        </div>
      </div>

      <Modal
        bodyClassName="!min-h-0 !overflow-hidden !px-0 !pt-0 !pb-0"
        className="h-[min(820px,calc(100dvh-32px))] !max-w-[1180px] grid-rows-[auto_minmax(0,1fr)_auto]"
        closeLabel={t('branding.workspaceBackground.cancel')}
        footer={
          <>
            <Button
              disabled={!settings.config.workspace_background}
              onClick={() => setResetOpen(true)}
              variant="dangerSoft"
            >
              {t('branding.workspaceBackground.delete')}
            </Button>
            <Button
              disabled={!canSave}
              isLoading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {t('branding.workspaceBackground.save')}
            </Button>
          </>
        }
        footerClassName="border-t border-[var(--color-border)] !px-5 !py-4"
        onClose={closeEditor}
        open={editorOpen}
        subtitle={t('branding.workspaceBackground.editorSubtitle')}
        title={t('branding.workspaceBackground.title')}
      >
        <div className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)]">
          <WorkspaceBackgroundToolbar
            device={device}
            onDeviceChange={setDevice}
            onPreviewStateChange={setPreviewState}
            previewState={previewState}
          />

          <div className="grid min-h-0 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_320px] lg:overflow-hidden">
            <main className="flex min-h-[420px] min-w-0 p-3 lg:min-h-0 lg:border-r lg:border-[var(--color-border)]">
              <div
                className={cn(
                  'flex min-h-0 w-full items-center justify-center overflow-hidden rounded-xl border border-[var(--color-border)] p-3 [container-type:size]',
                  'bg-[linear-gradient(45deg,var(--color-surface-muted)_25%,transparent_25%),linear-gradient(-45deg,var(--color-surface-muted)_25%,transparent_25%),linear-gradient(45deg,transparent_75%,var(--color-surface-muted)_75%),linear-gradient(-45deg,transparent_75%,var(--color-surface-muted)_75%)] bg-[length:20px_20px] bg-[position:0_0,0_10px,10px_-10px,-10px_0px]',
                )}
                data-testid="workspace-background-preview-stage"
              >
                <WorkspaceBackgroundPreview
                  baseColor={draft.baseColor}
                  crop={currentCrop}
                  device={device}
                  hubAgentName={settings.config.hub_agent_name ?? 'Hub Agent'}
                  imageUrl={previewUrl}
                  imageOpacity={draft.imageOpacity}
                  mobileConversationMode={draft.mobileConversationMode}
                  previewState={previewState}
                  userBubbleColor={userBubbleColor}
                  welcomeTitle={
                    configuredWelcomeTitle ?? t('branding.workspaceBackground.previewWelcomeTitle')
                  }
                />
              </div>
            </main>

            <aside
              className="min-h-0 border-t border-[var(--color-border)] lg:overflow-y-auto lg:border-t-0"
              data-testid="workspace-background-inspector"
            >
              <InspectorSection title={t('branding.workspaceBackground.composition')}>
                <RangeField
                  formatValue={(value) => `${value.toFixed(2)}×`}
                  label={t('branding.workspaceBackground.zoom')}
                  max={3}
                  min={1}
                  onChange={(zoom) => updateCrop({ zoom })}
                  step={0.05}
                  value={currentCrop.zoom}
                />
                <RangeField
                  formatValue={(value) => `${String(Math.round(value * 100))}%`}
                  label={t('branding.workspaceBackground.horizontalFocus')}
                  max={1}
                  min={0}
                  onChange={(focus_x) => updateCrop({ focus_x })}
                  step={0.01}
                  value={currentCrop.focus_x}
                />
                <RangeField
                  formatValue={(value) => `${String(Math.round(value * 100))}%`}
                  label={t('branding.workspaceBackground.verticalFocus')}
                  max={1}
                  min={0}
                  onChange={(focus_y) => updateCrop({ focus_y })}
                  step={0.01}
                  value={currentCrop.focus_y}
                />
                <Button
                  className="w-full"
                  onClick={() => updateCrop(defaultCrop)}
                  size="sm"
                  variant="secondary"
                >
                  {t('branding.workspaceBackground.resetCrop')}
                </Button>
              </InspectorSection>

              <InspectorSection title={t('branding.workspaceBackground.assetsSection')}>
                <UploadField
                  file={primaryFile}
                  hasStoredFile={hasPrimarySource}
                  imageUrl={primaryPreviewUrl}
                  inputRef={primaryInputRef}
                  label={t('branding.workspaceBackground.primaryImage')}
                  onChange={(event) => selectFile(event, setPrimaryFile)}
                  onSelect={() => primaryInputRef.current?.click()}
                />
                <UploadField
                  file={mobileFile}
                  hasStoredFile={hasMobileOverride && !removeMobileOverride}
                  imageUrl={mobilePreviewUrl}
                  inputRef={mobileInputRef}
                  label={t('branding.workspaceBackground.mobileImage')}
                  onChange={(event) => {
                    selectFile(event, setMobileFile)
                    setRemoveMobileOverride(false)
                  }}
                  onRemove={
                    hasMobileOverride || mobileFile
                      ? () => {
                          setMobileFile(null)
                          setRemoveMobileOverride(true)
                        }
                      : undefined
                  }
                  onSelect={() => mobileInputRef.current?.click()}
                />
              </InspectorSection>

              <InspectorSection title={t('branding.workspaceBackground.displaySection')}>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-[var(--color-text)]">
                    {t('branding.workspaceBackground.enabled')}
                  </span>
                  <Switch
                    aria-label={t('branding.workspaceBackground.enabled')}
                    checked={draft.enabled}
                    onCheckedChange={(enabled) => setDraft((current) => ({ ...current, enabled }))}
                  />
                </div>
                <div className="grid gap-2">
                  <RangeField
                    formatValue={(value) => `${String(Math.round(value * 100))}%`}
                    label={t('branding.workspaceBackground.imageOpacity')}
                    max={0.7}
                    min={0.1}
                    onChange={(imageOpacity) =>
                      setDraft((current) => ({ ...current, imageOpacity }))
                    }
                    step={0.01}
                    value={draft.imageOpacity}
                  />
                  <p className="text-[11px] leading-4 text-[var(--color-text-muted)]">
                    {t('branding.workspaceBackground.imageOpacityHint')}
                  </p>
                </div>
                <label className="grid gap-2 text-sm font-medium text-[var(--color-text)]">
                  {t('branding.workspaceBackground.baseColor')}
                  <div className="flex gap-2">
                    <input
                      aria-label={t('branding.workspaceBackground.baseColor')}
                      className="h-10 w-12 cursor-pointer rounded-lg border border-[var(--color-control-border)] bg-white p-1"
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          baseColor: event.target.value.toUpperCase(),
                        }))
                      }
                      type="color"
                      value={draft.baseColor}
                    />
                    <Input readOnly value={draft.baseColor} />
                  </div>
                </label>
                <label className="grid gap-2 text-sm font-medium text-[var(--color-text)]">
                  {t('branding.workspaceBackground.mobileConversationMode')}
                  <Select
                    onValueChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        mobileConversationMode:
                          value as WorkspaceBackgroundDraft['mobileConversationMode'],
                      }))
                    }
                    options={(['base_color', 'background'] as const).map((value) => ({
                      label: t(`branding.workspaceBackground.mobileModes.${value}`),
                      value,
                    }))}
                    value={draft.mobileConversationMode}
                  />
                </label>
              </InspectorSection>
            </aside>
          </div>
        </div>
      </Modal>

      <Modal
        closeLabel={t('branding.workspaceBackground.cancel')}
        footer={
          <>
            <Button onClick={() => setResetOpen(false)} size="sm" variant="secondary">
              {t('branding.workspaceBackground.cancel')}
            </Button>
            <Button
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              size="sm"
              variant="danger"
            >
              {t('branding.workspaceBackground.confirmDelete')}
            </Button>
          </>
        }
        onClose={() => setResetOpen(false)}
        open={resetOpen}
        title={t('branding.workspaceBackground.deleteTitle')}
      >
        <p className="text-sm leading-6 text-[var(--color-text)]">
          {t('branding.workspaceBackground.deleteDescription')}
        </p>
      </Modal>
    </section>
  )
}

function WorkspaceBackgroundToolbar({
  device,
  onDeviceChange,
  onPreviewStateChange,
  previewState,
}: {
  device: WorkspaceBackgroundVariant
  onDeviceChange: (device: WorkspaceBackgroundVariant) => void
  onPreviewStateChange: (state: 'conversation' | 'empty') => void
  previewState: 'conversation' | 'empty'
}) {
  const { t } = useTranslation('adminSettings')

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
          {t('branding.workspaceBackground.devicePreview')}
        </span>
        <div className="flex min-w-0 gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-1">
          {variants.map((variant) => (
            <button
              aria-pressed={device === variant}
              className={cn(
                'grid min-w-0 cursor-pointer rounded-md px-3 py-1 text-left transition-colors',
                device === variant
                  ? 'bg-white text-[var(--color-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
              )}
              key={variant}
              onClick={() => onDeviceChange(variant)}
              type="button"
            >
              <span className="text-xs font-medium whitespace-nowrap">
                {t(`branding.workspaceBackground.devices.${variant}`)}
              </span>
              <span className="text-[11px] leading-4 whitespace-nowrap opacity-75">
                {deviceResolutions[variant]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-[var(--color-text-muted)]">
          {t('branding.workspaceBackground.previewState')}
        </span>
        <div className="flex gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-1">
          {(['empty', 'conversation'] as const).map((state) => (
            <button
              aria-pressed={previewState === state}
              className={cn(
                'cursor-pointer rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                previewState === state
                  ? 'bg-white text-[var(--color-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
              )}
              key={state}
              onClick={() => onPreviewStateChange(state)}
              type="button"
            >
              {t(
                state === 'empty'
                  ? 'branding.workspaceBackground.emptyState'
                  : 'branding.workspaceBackground.conversationState',
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function InspectorSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section className="grid gap-4 border-b border-[var(--color-border)] p-4 last:border-b-0">
      <h3 className="text-sm font-semibold text-[var(--color-text)]">{title}</h3>
      {children}
    </section>
  )
}

function RangeField({
  formatValue,
  label,
  max,
  min,
  onChange,
  step,
  value,
}: {
  formatValue: (value: number) => string
  label: string
  max: number
  min: number
  onChange: (value: number) => void
  step: number
  value: number
}) {
  return (
    <label className="grid gap-2 text-xs font-medium text-[var(--color-text)]">
      <span className="flex justify-between gap-2">
        {label}
        <span className="font-mono text-[var(--color-primary)]">{formatValue(value)}</span>
      </span>
      <input
        className="w-full accent-[var(--color-primary)]"
        max={max}
        min={min}
        onChange={(event) => onChange(Number(event.target.value))}
        step={step}
        type="range"
        value={value}
      />
    </label>
  )
}

function UploadField({
  file,
  hasStoredFile,
  imageUrl,
  inputRef,
  label,
  onChange,
  onRemove,
  onSelect,
}: {
  file: File | null
  hasStoredFile: boolean
  imageUrl: string | null
  inputRef: RefObject<HTMLInputElement | null>
  label: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  onRemove?: () => void
  onSelect: () => void
}) {
  const { t } = useTranslation('adminSettings')

  return (
    <div className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2.5">
      <input
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onChange}
        ref={inputRef}
        type="file"
      />
      <span
        aria-hidden
        className="h-10 w-11 rounded-md border border-[var(--color-border)] bg-white bg-cover bg-center"
        style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
      />
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-[var(--color-text)]">{label}</span>
        <span className="mt-0.5 block truncate text-[11px] text-[var(--color-text-muted)]">
          {file?.name ??
            (hasStoredFile
              ? t('branding.workspaceBackground.storedImage')
              : t('branding.workspaceBackground.noImage'))}
        </span>
      </span>
      <span className="flex gap-1">
        {onRemove ? (
          <Button onClick={onRemove} size="sm" variant="ghost">
            {t('branding.workspaceBackground.removeImage')}
          </Button>
        ) : null}
        <Button onClick={onSelect} size="sm" variant="secondary">
          {t('branding.workspaceBackground.chooseImage')}
        </Button>
      </span>
    </div>
  )
}

function WorkspaceBackgroundPreview({
  baseColor,
  crop,
  device,
  hubAgentName,
  imageUrl,
  imageOpacity,
  mobileConversationMode,
  previewState,
  userBubbleColor,
  welcomeTitle,
}: {
  baseColor: string
  crop: WorkspaceBackgroundCrop
  device: WorkspaceBackgroundVariant
  hubAgentName: string
  imageUrl: string | null
  imageOpacity: number
  mobileConversationMode: WorkspaceBackgroundDraft['mobileConversationMode']
  previewState: 'conversation' | 'empty'
  userBubbleColor: string
  welcomeTitle: string
}) {
  const { t } = useTranslation('adminSettings')
  const mobile = device === 'mobile'
  const laptop = device === 'laptop'
  const hideMobileImage =
    mobile && previewState === 'conversation' && mobileConversationMode === 'base_color'
  const focusXPercent = String(crop.focus_x * 100)
  const focusYPercent = String(crop.focus_y * 100)

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border border-[var(--color-border)] bg-white shadow-sm',
        mobile
          ? 'w-[min(100%,46.2085cqh)] aspect-[390/844]'
          : laptop
            ? 'w-[min(100%,160cqh)] aspect-[16/10]'
            : 'w-[min(100%,177.7778cqh)] aspect-video',
      )}
      data-testid={`workspace-background-preview-${device}`}
    >
      {!mobile ? (
        <aside className="absolute inset-y-0 left-0 z-30 w-[18%] border-r border-[var(--color-border)] bg-white/96 p-3">
          <span className="block h-8 w-8 rounded-lg bg-[var(--color-primary-soft)]" />
          <span className="mt-4 block h-6 w-full rounded-md bg-[var(--color-primary-soft)]" />
        </aside>
      ) : null}
      <div
        className={cn('absolute inset-0 overflow-hidden', !mobile && 'left-[18%]')}
        style={{ backgroundColor: baseColor }}
      >
        {imageUrl && !hideMobileImage ? (
          <img
            alt=""
            className="pointer-events-none absolute inset-0 size-full object-cover"
            src={imageUrl}
            style={{
              objectPosition: `${focusXPercent}% ${focusYPercent}%`,
              opacity: imageOpacity,
              transform: `scale(${String(crop.zoom)})`,
              transformOrigin: `${focusXPercent}% ${focusYPercent}%`,
            }}
          />
        ) : null}
        <div
          className={cn(
            'pointer-events-none absolute inset-0',
            previewState === 'conversation' && !mobile && 'mx-auto w-[72%]',
          )}
          style={{ backgroundImage: getWorkspaceBackgroundVeil(baseColor, previewState) }}
        />
      </div>

      {mobile ? (
        <header className="absolute inset-x-0 top-0 z-40 h-[7%] border-b border-[rgba(91,125,174,0.18)] bg-transparent">
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] font-medium text-[var(--color-text)]">
            {hubAgentName}
          </span>
        </header>
      ) : previewState === 'conversation' ? (
        <header className="absolute top-0 right-0 left-[18%] z-40 flex h-[8%] items-center border-b border-[rgba(91,125,174,0.18)] bg-transparent px-[2.5%]">
          <span className="text-[9px] font-semibold text-[var(--color-text)]">{hubAgentName}</span>
          <span className="absolute left-1/2 max-w-[42%] -translate-x-1/2 truncate text-[8px] font-medium text-[var(--color-text-muted)]">
            {t('branding.workspaceBackground.previewConversationTitle')}
          </span>
        </header>
      ) : null}

      {previewState === 'empty' ? (
        <div
          className={cn(
            'pointer-events-none absolute z-40 flex flex-col items-center text-center',
            mobile ? 'inset-x-4 top-[42%]' : 'right-0 left-[18%] top-[35%]',
          )}
        >
          <strong className={cn('text-[#102447]', mobile ? 'text-sm' : 'text-base')}>
            {welcomeTitle}
          </strong>
        </div>
      ) : (
        <div
          className={cn(
            'pointer-events-none absolute z-40 flex flex-col',
            mobile ? 'inset-x-5 top-[15%] gap-3' : 'right-[12%] left-[30%] top-[16%] gap-3',
          )}
          data-testid="workspace-background-conversation-preview"
        >
          <span
            className={cn(
              'ml-auto max-w-[76%] rounded-[18px]',
              mobile ? 'px-2.5 py-1.5 text-[8px] leading-3' : 'px-3 py-2 text-[9px] leading-4',
            )}
            data-testid="workspace-background-preview-user-message"
            style={{
              backgroundColor: userBubbleColor,
              color: resolveChatUserBubbleTextColor(userBubbleColor),
            }}
          >
            {t('branding.workspaceBackground.previewConversationUser')}
          </span>
          <span
            className={cn(
              'max-w-[82%] text-[var(--color-text)]',
              mobile ? 'text-[8px] leading-3.5' : 'text-[9px] leading-4',
            )}
            data-testid="workspace-background-preview-assistant-message"
          >
            {t('branding.workspaceBackground.previewConversationAssistant')}
          </span>
        </div>
      )}

      <div
        className={cn(
          'absolute z-40 border border-[#D5DFEC] bg-white/96 shadow-sm',
          mobile
            ? 'inset-x-3 bottom-3 h-[7%] rounded-full'
            : previewState === 'empty'
              ? 'right-[10%] left-[28%] top-[49%] h-[17%] rounded-2xl'
              : 'right-[10%] bottom-[14%] left-[28%] h-[15%] rounded-2xl',
        )}
      />
    </div>
  )
}
